import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import {
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  Typography,
  Box,
  Link,
} from "@material-ui/core";
import { shouldAuthenticate } from "utils/authentication";
import AuthenticationModal from "components/AuthenticationModal";
import ButtonWithLoading from "components/ButtonWithLoading";
import SwitchField from "components/Forms/SwitchField";
import DashboardSecuritySettingsLoader from "./Loader";
import SamlBtn from "./SamlBtn";
import { fetchSecuritySettings, saveSecuritySettings } from "../api";
import { useStyles } from "../styled";

import { LoginOptions } from "@dashboard-v3/api";

type Status = "IDLE" | "SAVING" | "FETCHING";

export default function DashboardSecuritySettings() {
  const { t } = useTranslation("security");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const classes = useStyles();
  const [authIsOpen, setAuthIsOpen] = useState(false);
  const [status, setStatus] = useState<Status>("IDLE");
  const [previousSamlConfig, setPreviousSamlConfig] = useState();
  const [settings, setSettings] = useState(initSettings);
  const isSamlPreviousLoaded = !!previousSamlConfig;

  useEffect(() => {
    async function getSettings() {
      setStatus("FETCHING");
      try {
        const result = await fetchSecuritySettings();
        let saml = null;

        if ("SAML" in result) {
          saml = {
            entityId: result.SAML.entityId,
            ssoUrl: result.SAML.ssoUrl,
          };
        }

        setSettings({
          google: "GOOGLE" in result,
          office: "OFFICE" in result,
          password: "PASSWORD" in result,
          saml,
          twoFactorAuthentication: result.twoFactorAuthEnabled ?? false,
        });

        setPreviousSamlConfig(saml);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("error.fetchSecuritySettings"), {
          variant: "error",
        });
      } finally {
        setStatus("IDLE");
      }
    }

    getSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function isSaveDisabled() {
    // eslint-disable-next-line no-use-before-define
    if (samlError()) return true;

    return (
      !settings.google &&
      !settings.office &&
      !settings.password &&
      !settings.saml
    );
  }

  const handleToggle = key => () => {
    setSettings(previous => {
      if (key === "saml") {
        const wasPreviousSelected = !!previous.saml;

        return {
          ...previous,
          saml: wasPreviousSelected ? null : previousSamlConfig || {},
        };
      }

      return { ...previous, [key]: !previous[key] };
    });
  };

  const handleSamlSettings = samlSettings => {
    setSettings(previous => {
      return { ...previous, saml: samlSettings };
    });
  };

  async function saveSettings() {
    setStatus("SAVING");

    const changes: LoginOptions = {};

    if (settings.google) {
      changes.GOOGLE = {};
    }

    if (settings.office) {
      changes.OFFICE = {};
    }

    if (settings.password) {
      changes.PASSWORD = {};
    }

    if (settings.saml) {
      changes.SAML = settings.saml;
    }

    changes.twoFactorAuthEnabled = settings.twoFactorAuthentication;

    try {
      await saveSecuritySettings(changes);
      enqueueSnackbar(t("dashboardSecurity.saveSuccess"), {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("error.saveAndGetSettings"), {
        variant: "error",
      });
    } finally {
      setStatus("IDLE");
    }
  }

  const handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    if (shouldAuthenticate()) {
      setAuthIsOpen(true);
    } else {
      saveSettings();
    }
  };

  const onAuthSuccess = () => {
    setAuthIsOpen(false);
    saveSettings();
  };

  const samlError = () => {
    if (!settings.saml) return false;

    const { cert, entityId, ssoUrl } = settings.saml;
    let isValid = entityId && ssoUrl;

    if (!isSamlPreviousLoaded) {
      isValid = isValid && cert;
    }

    return !isValid;
  };

  const SamlOptionLabel = () => {
    return (
      <div>
        SAML
        {samlError() && (
          <Typography
            display="inline"
            variant="body2"
            color="error"
            style={{ marginLeft: 5 }}
          >
            {t("credentialsMustBeComplete")}
          </Typography>
        )}
      </div>
    );
  };

  if (status === "FETCHING") {
    return <DashboardSecuritySettingsLoader />;
  }

  return (
    <Paper className={classes.container}>
      <Box>
        <Typography>{t("dashboardSecurity.title")}</Typography>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            {t("dashboardSecurity.subtitle")}
          </Typography>{" "}
          <Typography variant="caption" color="textSecondary">
            {t("toSeeAuthorizedUsers")}{" "}
            <Link
              component="button"
              variant="button"
              style={{ fontSize: "0.75rem" }}
              onClick={() => history.push("/users")}
            >
              {t("common:user_plural")}
            </Link>
          </Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <List data-testid={"security-options"}>
          <ListItem>
            <SwitchField
              name="password"
              label={t("userAndPassword")}
              description={t("allowSignInWithMxhero")}
              value={settings.password}
              onChange={handleToggle("password")}
            />
          </ListItem>
          <Divider variant="middle" />
          <ListItem>
            <SwitchField
              name="google"
              label={t("common:googleApps")}
              description={t("allowSignInWithGoogle")}
              value={settings.google}
              onChange={handleToggle("google")}
            />
          </ListItem>
          <Divider variant="middle" />
          <ListItem>
            <SwitchField
              name="office365"
              label={t("common:office365")}
              description={t("allowSignInWithOffice")}
              value={settings.office}
              onChange={handleToggle("office")}
            />
          </ListItem>
          <Divider variant="middle" />
          <ListItem>
            <SwitchField
              name="saml"
              label={<SamlOptionLabel />}
              description={t("allowSignInWithSaml")}
              value={!!settings.saml}
              onChange={handleToggle("saml")}
            />
            <ListItemSecondaryAction>
              <SamlBtn
                settings={settings.saml}
                color={samlError() ? "secondary" : "default"}
                certIsRequired={!isSamlPreviousLoaded}
                disabled={!settings.saml}
                onSave={handleSamlSettings}
              />
            </ListItemSecondaryAction>
          </ListItem>
          <Divider variant="middle" />
          <ListItem>
            <SwitchField
              name="2fa"
              label={t("2FA.option")}
              description={t("2FA.description")}
              value={settings.twoFactorAuthentication}
              onChange={handleToggle("twoFactorAuthentication")}
            />
          </ListItem>
        </List>

        <ButtonWithLoading
          loading={status === "SAVING"}
          size="small"
          disableElevation
          data-testid="options__save-btn"
          disabled={isSaveDisabled()}
          variant="contained"
          color="primary"
          style={{ marginTop: "10px" }}
          type="submit"
        >
          {t("saveSettings")}
        </ButtonWithLoading>
      </form>

      {authIsOpen && (
        <AuthenticationModal
          isOpen={authIsOpen}
          onSuccess={onAuthSuccess}
          onClose={() => setAuthIsOpen(false)}
        />
      )}
    </Paper>
  );
}

function initSettings() {
  return {
    google: false,
    office: false,
    password: false,
    saml: null,
    twoFactorAuthentication: false,
  };
}
